import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

/**
 * Hero search bar — uses NATIVE HTML <select> elements for maximum
 * cross-browser/device compatibility (works on iOS, Android, all desktops).
 */
export default function HeroSearchBar() {
  const [localities, setLocalities] = useState({ pcmc: [], pune: [] });
  const [categories, setCategories] = useState([]);
  const [loc, setLoc] = useState("");
  const [cat, setCat] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    api.get("/meta/localities").then((r) => setLocalities({ pcmc: r.data.pcmc || [], pune: r.data.pune || [] })).catch(() => {});
    api.get("/meta/categories").then((r) => setCategories(r.data.categories || [])).catch(() => {});
  }, []);

  const submit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const params = new URLSearchParams();
    if (loc) params.set("locality", loc);
    if (cat) params.set("category", cat);
    nav(`/spas?${params.toString()}`);
  };

  const selectClass =
    "mt-1 w-full h-11 rounded-xl border border-[rgba(17,24,39,0.14)] bg-white px-3 pr-9 text-sm text-[#111827] appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#C9A86A] focus:border-transparent";

  return (
    <form
      onSubmit={submit}
      className="bg-white rounded-2xl p-4 sm:p-5 shadow-[0_20px_50px_rgba(17,24,39,0.10)] border border-[#E7DDCF]"
    >
      <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
        <div className="sm:col-span-5">
          <label className="text-[11px] uppercase tracking-wider text-[#4B5563] font-medium">Locality</label>
          <div className="relative">
            <select
              data-testid="hero-locality-select"
              value={loc}
              onChange={(e) => setLoc(e.target.value)}
              className={selectClass}
            >
              <option value="">Choose area (e.g. Wakad)</option>
              {localities.pcmc.length > 0 && (
                <optgroup label="PCMC">
                  {localities.pcmc.map((l) => <option key={l} value={l}>{l}</option>)}
                </optgroup>
              )}
              {localities.pune.length > 0 && (
                <optgroup label="Pune">
                  {localities.pune.map((l) => <option key={l} value={l}>{l}</option>)}
                </optgroup>
              )}
            </select>
            <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 mt-0.5 text-[#4B5563] pointer-events-none" />
          </div>
        </div>
        <div className="sm:col-span-5">
          <label className="text-[11px] uppercase tracking-wider text-[#4B5563] font-medium">Category</label>
          <div className="relative">
            <select
              data-testid="hero-category-select"
              value={cat}
              onChange={(e) => setCat(e.target.value)}
              className={selectClass}
            >
              <option value="">Spa type (e.g. Ayurvedic)</option>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 mt-0.5 text-[#4B5563] pointer-events-none" />
          </div>
        </div>
        <div className="sm:col-span-2 flex items-end">
          <Button
            type="submit"
            data-testid="hero-search-submit-button"
            className="w-full h-11 bg-[#111827] hover:bg-[#0B1220] text-white"
          >
            Search
          </Button>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <span className="text-xs text-[#4B5563]">Popular:</span>
        {["Luxury Spa", "Ayurvedic Spa", "Couples Spa", "Thai Spa"].map((q) => (
          <button
            type="button"
            key={q}
            onClick={() => setCat(q)}
            data-testid={`hero-quick-chip-${q.toLowerCase().replace(/\s/g, '-')}`}
            className={`text-xs px-3 py-1 rounded-full border transition-colors ${
              cat === q
                ? "bg-[#1F4E3D] text-white border-[#1F4E3D]"
                : "bg-[#F5EFE6] border-[rgba(17,24,39,0.08)] hover:bg-[#EFE7D8]"
            }`}
          >
            {q}
          </button>
        ))}
      </div>
    </form>
  );
}
